import {
  getProjectPrerequisite,
  prerequisiteFromError
} from '../services/projectPrerequisite'
import {
  isPublicRouteId,
  resolveRouteValue
} from '../common/routeRecordId'

export const PROJECT_PREREQUISITE_ROUTE = 'ProjectPrerequisiteRequired'

/**
 * 自分で入力する画面。ここは前提を確かめる。
 *
 * プロジェクトに所属していないと、保存しようとして初めて弾かれる。
 * 先に案内を出したい。
 */
const SELF_ROUTE_NAMES = new Set([
  '勤務表',
  '業務報告'
])

/**
 * 管理者が他人のぶんを**見る**画面。
 *
 * **ここでは前提を確かめない。**（2026-09-10、テスターの E-15）
 *
 * 以前は勤務表詳細・業務報告詳細もここに並べていた。だが
 * <b>読むだけの画面で、対象者の入力前提を要求するのは筋が通らない。</b>
 * 対象者が退職して案件を閉じていると `projectConfigured` が偽になり、
 * 過去の勤務表が**永久に開けなくなる**。実際、無効にした利用者の
 * 氏名を押すと `?reason=PROJECT_PREREQUISITE_FORBIDDEN` で
 * ACCESS DENIED になっていた。
 *
 * 会社の境目は back が見る（`/admin/rosterSelect` の
 * `requireSameAccountUserAnyState`）。**ここを空にしても、他社の
 * 勤務表が読めるようにはならない。**
 *
 * 空の Set を残しているのは、`mode: 'target'` の枝ごと消さないため。
 * 別の画面で「対象者の前提を確かめたい」が出たときに、ここへ足せば
 * 済むようにしておく。
 */
const TARGET_ROUTE_NAMES = new Set([])

let installed = false

export function projectRequirementForRoute (route) {
  if (!route || route.name === PROJECT_PREREQUISITE_ROUTE) return null
  if (SELF_ROUTE_NAMES.has(route.name)) {
    return { mode: 'self', targetUserId: null }
  }
  if (!TARGET_ROUTE_NAMES.has(route.name)) return null

  const targetUserId = resolveRouteValue(route, 'user_id', 'USER') ||
    resolveRouteValue(route, 'user_ref', 'USER')
  if (!Number.isInteger(targetUserId) || targetUserId <= 0) {
    return { mode: 'invalid', targetUserId: null }
  }
  return { mode: 'target', targetUserId }
}

function prerequisiteRedirect (to, requirement, prerequisite) {
  const query = {
    from: to.fullPath,
    reason: prerequisite.reasonCode,
    scope: requirement.mode
  }
  const params = {}
  if (requirement.targetUserId != null) {
    const userRef = to.params && to.params.user_ref
    if (isPublicRouteId(userRef)) {
      query.targetRef = String(userRef).toLowerCase()
    } else {
      // encode APIの一時障害時も、数値IDをURLへ戻さず同一画面内だけ保持する。
      params.targetUserId = requirement.targetUserId
    }
  }
  return {
    name: PROJECT_PREREQUISITE_ROUTE,
    params,
    query,
    replace: true
  }
}

export default function installProjectPrerequisiteGuard (router) {
  if (installed) return
  installed = true

  router.beforeEach((to, from, next) => {
    const requirement = projectRequirementForRoute(to)
    if (!requirement) {
      next()
      return
    }
    if (requirement.mode === 'invalid') {
      next({
        name: 'AccessDenied',
        query: { reason: 'TARGET_USER_INVALID' },
        replace: true
      })
      return
    }

    getProjectPrerequisite({ targetUserId: requirement.targetUserId })
      .then(prerequisite => {
        if (prerequisite.projectConfigured) {
          next()
          return
        }
        next(prerequisiteRedirect(to, requirement, prerequisite))
      })
      .catch(error => {
        const status = error && error.response ? Number(error.response.status) : 0
        const prerequisite = prerequisiteFromError(error)
        if (prerequisite) {
          next(prerequisiteRedirect(to, requirement, prerequisite))
          return
        }
        if (status === 401) {
          next({ name: 'Login' })
          return
        }
        next({
          name: 'AccessDenied',
          query: {
            reason: status === 403
              ? 'PROJECT_PREREQUISITE_FORBIDDEN'
              : (error && error.code) || 'PROJECT_PREREQUISITE_ERROR'
          },
          replace: true
        })
      })
  })
}
